import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { HeartPulse, Plus, Loader2, CheckCircle2, Clock, AlertTriangle, Circle, Bell, Send, ClipboardList, ChevronRight, Flag, ShieldAlert, ArrowUpRight, MessageCircle } from "lucide-react";
import { ProgrammePipeline, CheckinAnswers, checkinWhatsApp } from "@/components/staff/FollowUpPipeline";

const FollowUpsPage = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState("checkins");
  const [activeStatFilter, setActiveStatFilter] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [checkins, setCheckins] = useState([]);
  const [totalCheckins, setTotalCheckins] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [stats, setStats] = useState(null);
  const [opFlags, setOpFlags] = useState([]);
  const [emergencyGuidance, setEmergencyGuidance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedCheckin, setExpandedCheckin] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", due_date: "", task_type: "follow_up", patient_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, [statusFilter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [tRes, cRes, aRes, sRes, fRes, eRes] = await Promise.all([
        fetch(`${API}/staff/tasks?limit=50${statusFilter !== "all" ? `&status=${statusFilter}` : ""}`, { headers }),
        fetch(`${API}/staff/checkins?limit=50`, { headers }),
        fetch(`${API}/staff/clinical-alerts?limit=30`, { headers }),
        fetch(`${API}/staff/checkins/stats`, { headers }),
        fetch(`${API}/staff/operational-flags`, { headers }),
        fetch(`${API}/staff/triage/emergency-guidance`, { headers }),
      ]);
      if (tRes.ok) { const d = await tRes.json(); setTasks(d.tasks); setTotalTasks(d.total); }
      if (cRes.ok) { const d = await cRes.json(); setCheckins(d.checkins); setTotalCheckins(d.total); }
      if (aRes.ok) { const d = await aRes.json(); setAlerts(d.alerts); setTotalAlerts(d.total); }
      if (sRes.ok) setStats(await sRes.json());
      if (fRes.ok) { const d = await fRes.json(); setOpFlags(d.flags); }
      if (eRes.ok) setEmergencyGuidance(await eRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreateTask = async () => {
    if (!form.title) { toast.error("Please enter a task title."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/staff/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (res.ok) { toast.success("Task created."); setShowNew(false); setForm({ title: "", description: "", priority: "medium", due_date: "", task_type: "follow_up", patient_id: "" }); fetchAll(); }
      else toast.error("Failed.");
    } catch { toast.error("Connection error."); }
    finally { setSaving(false); }
  };

  const handleTaskStatus = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${API}/staff/tasks/${taskId}/status?status=${newStatus}`, { method: "PUT", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success(`Task ${newStatus}.`); fetchAll(); }
    } catch { toast.error("Failed."); }
  };

  const handleSendCheckin = async (checkinId) => {
    try {
      const res = await fetch(`${API}/staff/checkins/${checkinId}/send`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data.emailed) toast.success(`Check-in emailed to ${data.patient_name}.`);
        else toast.error(`Email could not be sent to ${data.patient_name}. Use WhatsApp instead.`);
        fetchAll();
      } else toast.error("Failed.");
    } catch { toast.error("Failed."); }
  };

  const handleAcknowledgeReview = async (checkinId) => {
    try {
      const res = await fetch(`${API}/staff/checkins/${checkinId}/acknowledge-review`, { method: "PUT", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Review marked as reviewed."); fetchAll(); }
    } catch { toast.error("Failed."); }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API}/staff/clinical-alerts/${alertId}/acknowledge`, { method: "PUT", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Alert acknowledged."); fetchAll(); }
    } catch { toast.error("Failed."); }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      const res = await fetch(`${API}/staff/clinical-alerts/${alertId}/resolve?notes=resolved`, { method: "PUT", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Alert resolved."); fetchAll(); }
    } catch { toast.error("Failed."); }
  };

  const handleEscalateAlert = async (alertId) => {
    try {
      const res = await fetch(`${API}/staff/alerts/${alertId}/escalate`, { method: "PUT", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchAll(); }
    } catch { toast.error("Failed."); }
  };

  const handleRunEscalation = async () => {
    try {
      const res = await fetch(`${API}/staff/triage/run-escalation`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchAll(); }
    } catch { toast.error("Failed."); }
  };

  const priorityColor = { low: "text-stone-400", medium: "text-blue-500", high: "text-[#FF7A00]", urgent: "text-red-500" };
  const triageColor = { 1: "bg-green-50 text-green-700 border-green-200", 2: "bg-yellow-50 text-yellow-700 border-yellow-200", 3: "bg-orange-50 text-orange-700 border-orange-200", 4: "bg-red-50 text-red-700 border-red-200" };
  const triageLabel = { 1: "Routine", 2: "Needs review", 3: "Priority clinical", 4: "Urgent" };
  const checkinStatusColor = { scheduled: "bg-stone-100 text-stone-600", sent: "bg-blue-50 text-blue-700 border-blue-200", completed: "bg-green-50 text-green-700 border-green-200", overdue: "bg-red-50 text-red-700 border-red-200", missed: "bg-red-50 text-red-700 border-red-200", skipped: "bg-stone-50 text-stone-400" };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="follow-ups-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Follow-ups and Triage</h1>
          <p className="mt-1 text-base text-stone-500">Weekly check-ins, clinical alerts, and task management.</p>
        </div>
        <Button onClick={() => setShowNew(!showNew)} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="new-task-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Task
        </Button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card className={`rounded-xl border-stone-200/60 p-3 cursor-pointer hover:shadow-md hover:border-[#FF7A00]/30 transition-all ${activeStatFilter === "total" ? "ring-2 ring-[#FF7A00]/40 bg-[#FFF8F2]" : ""}`} onClick={() => { setActiveStatFilter(activeStatFilter === "total" ? null : "total"); setTab("checkins"); }}><p className="text-2xl font-bold text-stone-900">{stats.total}</p><p className="text-xs text-stone-500">Total check-ins</p></Card>
          <Card className={`rounded-xl border-stone-200/60 p-3 cursor-pointer hover:shadow-md hover:border-[#FF7A00]/30 transition-all ${activeStatFilter === "sent" ? "ring-2 ring-[#FF7A00]/40 bg-[#FFF8F2]" : ""}`} onClick={() => { setActiveStatFilter(activeStatFilter === "sent" ? null : "sent"); setTab("checkins"); }}><p className="text-2xl font-bold text-blue-600">{stats.sent}</p><p className="text-xs text-stone-500">Sent</p></Card>
          <Card className={`rounded-xl border-stone-200/60 p-3 cursor-pointer hover:shadow-md hover:border-[#FF7A00]/30 transition-all ${activeStatFilter === "completed" ? "ring-2 ring-[#FF7A00]/40 bg-[#FFF8F2]" : ""}`} onClick={() => { setActiveStatFilter(activeStatFilter === "completed" ? null : "completed"); setTab("checkins"); }}><p className="text-2xl font-bold text-green-600">{stats.completed}</p><p className="text-xs text-stone-500">Completed</p></Card>
          <Card className={`rounded-xl border-stone-200/60 p-3 cursor-pointer hover:shadow-md hover:border-[#FF7A00]/30 transition-all ${activeStatFilter === "needs_review" ? "ring-2 ring-[#FF7A00]/40 bg-[#FFF8F2]" : ""}`} onClick={() => { setActiveStatFilter(activeStatFilter === "needs_review" ? null : "needs_review"); setTab("checkins"); }}><p className="text-2xl font-bold text-[#FF7A00]">{stats.triage.needs_review + stats.triage.priority}</p><p className="text-xs text-stone-500">Needs review</p></Card>
          <Card className={`rounded-xl border-stone-200/60 p-3 cursor-pointer hover:shadow-md hover:border-[#FF7A00]/30 transition-all ${activeStatFilter === "alerts" ? "ring-2 ring-[#FF7A00]/40 bg-[#FFF8F2]" : ""}`} onClick={() => { setActiveStatFilter(activeStatFilter === "alerts" ? null : "alerts"); setTab("alerts"); }}><p className="text-2xl font-bold text-red-500">{stats.unacknowledged_alerts}</p><p className="text-xs text-stone-500">Unack. alerts</p></Card>
          <Card className={`rounded-xl border-stone-200/60 p-3 cursor-pointer hover:shadow-md hover:border-[#FF7A00]/30 transition-all ${activeStatFilter === "tasks" ? "ring-2 ring-[#FF7A00]/40 bg-[#FFF8F2]" : ""}`} onClick={() => { setActiveStatFilter(activeStatFilter === "tasks" ? null : "tasks"); setTab("tasks"); }}><p className="text-2xl font-bold text-stone-900">{totalTasks}</p><p className="text-xs text-stone-500">Open tasks</p></Card>
        </div>
      )}

      {stats?.programme && <ProgrammePipeline stats={stats} />}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 rounded-lg">
          <TabsTrigger value="checkins" className="rounded-md text-sm" data-testid="tab-checkins">Check-ins ({totalCheckins})</TabsTrigger>
          <TabsTrigger value="alerts" className="rounded-md text-sm" data-testid="tab-alerts">Clinical Alerts ({totalAlerts})</TabsTrigger>
          <TabsTrigger value="tasks" className="rounded-md text-sm" data-testid="tab-tasks">Tasks ({totalTasks})</TabsTrigger>
          <TabsTrigger value="flags" className="rounded-md text-sm" data-testid="tab-flags">Operational Flags ({opFlags.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* New task form */}
      {showNew && (
        <Card className="rounded-xl border-[#FF7A00]/30 p-5 space-y-3" data-testid="task-form">
          <h3 className="font-bold text-stone-900">Create task</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <Input placeholder="Task title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-lg" data-testid="task-title-input" />
            <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}><SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent></Select>
            <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="rounded-lg" />
          </div>
          <Textarea placeholder="Description..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-lg" rows={2} />
          <div className="flex gap-2">
            <Button onClick={handleCreateTask} disabled={saving} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Create</Button>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* TAB: Check-ins */}
      {tab === "checkins" && (
        <div className="space-y-2">
          {checkins.length > 0 ? checkins.map(c => (
            <Card key={c.checkin_id} className="rounded-xl border-stone-200/60 p-4" data-testid={`checkin-${c.checkin_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 cursor-pointer" onClick={() => setExpandedCheckin(expandedCheckin === c.checkin_id ? null : c.checkin_id)}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${c.triage_level >= 3 ? "bg-red-100" : c.triage_level === 2 ? "bg-yellow-100" : c.status === "completed" ? "bg-green-100" : "bg-stone-100"}`}>
                    {c.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <ClipboardList className="w-4 h-4 text-stone-500" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-stone-900">{c.patient_name} - {c.programme_week}</p>
                    <p className="text-xs text-stone-400">Due: {c.due_date?.split("T")[0]}{c.completed_at ? ` | Completed: ${c.completed_at.split("T")[0]}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-xs capitalize ${checkinStatusColor[c.status] || ""}`}>{c.status}</Badge>
                  {c.triage_level && c.triage_level > 1 && (
                    <Badge variant="outline" className={`text-xs ${triageColor[c.triage_level] || ""}`}>{triageLabel[c.triage_level]}</Badge>
                  )}
                  {c.booking_status && c.booking_status !== "not_requested" && (
                    <Badge variant="outline" className="text-xs capitalize" data-testid={`booking-status-${c.checkin_id}`}>{c.booking_status.replace(/_/g, " ")}</Badge>
                  )}
                  {c.status === "scheduled" && (
                    <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={e => { e.stopPropagation(); handleSendCheckin(c.checkin_id); }} data-testid={`send-checkin-${c.checkin_id}`}>
                      <Send className="w-3 h-3 mr-1" /> Email now
                    </Button>
                  )}
                  {["scheduled", "sent", "missed"].includes(c.status) && (
                    <Button size="sm" variant="outline" className="text-xs rounded-lg text-green-700 border-green-200" onClick={e => { e.stopPropagation(); checkinWhatsApp(c); }} data-testid={`whatsapp-checkin-${c.checkin_id}`}>
                      <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                    </Button>
                  )}
                  {c.checkin_type === "programme_review" && c.status === "completed" && !c.review_acknowledged && (
                    <Button size="sm" className="text-xs rounded-lg bg-stone-900 text-white" onClick={e => { e.stopPropagation(); handleAcknowledgeReview(c.checkin_id); }} data-testid={`ack-review-${c.checkin_id}`}>
                      Mark reviewed
                    </Button>
                  )}
                  <ChevronRight className={`w-4 h-4 text-stone-400 transition-transform ${expandedCheckin === c.checkin_id ? "rotate-90" : ""}`} />
                </div>
              </div>

              {expandedCheckin === c.checkin_id && c.response && (c.response.feeling || c.checkin_type === "programme_review") && (
                <CheckinAnswers checkin={c} />
              )}
              {expandedCheckin === c.checkin_id && c.response && !c.response.feeling && c.checkin_type !== "programme_review" && (
                <div className="mt-3 pt-3 border-t border-stone-100 space-y-2 text-sm">
                  <div><span className="text-xs font-semibold text-stone-400">Progress: </span><span className="text-stone-700 capitalize">{c.response.general_progress?.replace(/_/g, " ")}</span></div>
                  <div><span className="text-xs font-semibold text-stone-400">Plan experience: </span><span className="text-stone-700 capitalize">{c.response.plan_experience?.replace(/_/g, " ")}</span></div>
                  {c.response.plan_difficulty && <div><span className="text-xs font-semibold text-stone-400">Difficulty: </span><span className="text-stone-700 capitalize">{c.response.plan_difficulty?.replace(/_/g, " ")}</span></div>}
                  {c.response.barriers && <div><span className="text-xs font-semibold text-stone-400">Barriers: </span><span className="text-stone-700">{c.response.barriers}</span></div>}
                  {c.response.new_symptoms && <div className="text-red-600"><span className="text-xs font-semibold">Symptoms: </span>{c.response.new_symptoms}</div>}
                  {c.response.medication_changes && <div className="text-orange-600"><span className="text-xs font-semibold">Med changes: </span>{c.response.medication_changes}</div>}
                  {c.response.fasting_glucose && <div><span className="text-xs font-semibold text-stone-400">Glucose: </span>{c.response.fasting_glucose} mmol/L</div>}
                  {c.response.blood_pressure && <div><span className="text-xs font-semibold text-stone-400">BP: </span>{c.response.blood_pressure}</div>}
                  {c.response.weight && <div><span className="text-xs font-semibold text-stone-400">Weight: </span>{c.response.weight} kg</div>}
                  {c.response.questions_for_dietitian && <div><span className="text-xs font-semibold text-stone-400">Questions: </span>{c.response.questions_for_dietitian}</div>}
                  {c.response.wants_to_speak && <Badge className="bg-[#FF7A00]/10 text-[#FF7A00] text-xs mt-2">Patient wants to speak with dietitian</Badge>}
                  {c.triage_flags?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-stone-100">
                      <p className="text-xs font-bold text-stone-500 mb-1">Triage flags:</p>
                      {c.triage_flags.map((f, i) => <p key={i} className="text-xs text-red-600">- {f.reason}</p>)}
                    </div>
                  )}
                </div>
              )}
            </Card>
          )) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <ClipboardList className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No check-ins scheduled. Schedule check-ins from a patient's profile.</p>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Clinical Alerts */}
      {tab === "alerts" && (
        <div className="space-y-3">
          {/* Emergency guidance banner */}
          {emergencyGuidance?.is_out_of_hours && (
            <Card className="rounded-xl border-amber-300 bg-amber-50 p-4" data-testid="ooh-banner">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-bold text-amber-800">Out of hours</span>
              </div>
              <p className="text-xs text-amber-700">{emergencyGuidance.out_of_hours_message}</p>
            </Card>
          )}

          {/* Emergency guidance (shown for level 4 alerts) */}
          {alerts.some(a => a.severity >= 4 && a.status !== "resolved") && (
            <Card className="rounded-xl border-red-300 bg-red-50 p-4" data-testid="emergency-guidance">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="w-5 h-5 text-red-600" />
                <span className="text-sm font-bold text-red-800">Emergency guidance</span>
              </div>
              <p className="text-xs text-red-700 whitespace-pre-line">{emergencyGuidance?.emergency_guidance}</p>
            </Card>
          )}

          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-stone-900">Clinical alerts ({totalAlerts})</h3>
            <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={handleRunEscalation} data-testid="run-escalation-btn">
              <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Run Escalation Check
            </Button>
          </div>

          {alerts.length > 0 ? alerts.map(a => (
            <Card key={a.alert_id} className={`rounded-xl p-4 ${a.severity >= 4 ? "border-red-300 bg-red-50/30" : a.severity >= 3 ? "border-orange-300 bg-orange-50/30" : "border-yellow-300 bg-yellow-50/30"}`} data-testid={`alert-${a.alert_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <Bell className={`w-5 h-5 shrink-0 ${a.severity >= 4 ? "text-red-500" : a.severity >= 3 ? "text-orange-500" : "text-yellow-500"}`} />
                  <div>
                    <p className="text-sm font-medium text-stone-900">{a.patient_name} - {a.source?.replace(/_/g, " ")}</p>
                    <p className="text-xs text-stone-500">{a.description || a.flags?.map(f => f.reason).join("; ")}</p>
                    <p className="text-xs text-stone-400 mt-0.5">
                      {a.created_at?.split("T")[0]}
                      {a.assigned_to_name && <> &middot; Assigned: {a.assigned_to_name}</>}
                      {a.escalation_count > 0 && <> &middot; Escalated {a.escalation_count}x</>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={`text-xs ${triageColor[a.severity] || ""}`}>{triageLabel[a.severity]}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{a.status}</Badge>
                  {a.status === "new" && (
                    <>
                      <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => handleAcknowledgeAlert(a.alert_id)}>Acknowledge</Button>
                      <Button size="sm" variant="outline" className="text-xs rounded-lg text-orange-600" onClick={() => handleEscalateAlert(a.alert_id)} data-testid={`escalate-${a.alert_id}`}>
                        <ArrowUpRight className="w-3 h-3 mr-1" /> Escalate
                      </Button>
                    </>
                  )}
                  {a.status === "acknowledged" && (
                    <>
                      <Button size="sm" className="text-xs rounded-lg bg-green-600 hover:bg-green-700 text-white" onClick={() => handleResolveAlert(a.alert_id)}>Resolve</Button>
                      <Button size="sm" variant="outline" className="text-xs rounded-lg text-orange-600" onClick={() => handleEscalateAlert(a.alert_id)}>
                        <ArrowUpRight className="w-3 h-3 mr-1" /> Escalate
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          )) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <Bell className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No clinical alerts.</p>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Operational Flags */}
      {tab === "flags" && (
        <div className="space-y-3" data-testid="flags-tab">
          <Card className="rounded-xl border-stone-200/60 p-4">
            <p className="text-xs text-stone-500">Operational flags are not clinical emergencies. They flag administrative items that need staff attention: incomplete forms, missed appointments, payment issues, and communication failures.</p>
          </Card>

          {opFlags.length > 0 ? opFlags.map((f, i) => {
            const severityStyle = f.severity === "urgent" ? "border-red-200 bg-red-50/30" : f.severity === "attention" ? "border-amber-200 bg-amber-50/30" : "border-stone-200";
            const icon = f.type === "unacknowledged_alert" ? ShieldAlert : f.type === "missed_checkin" ? Clock : f.type === "failed_communication" ? AlertTriangle : Flag;
            const IconComp = icon;
            return (
              <Card key={i} className={`rounded-xl p-4 flex items-center gap-3 ${severityStyle}`} data-testid={`flag-${i}`}>
                <IconComp className={`w-5 h-5 shrink-0 ${f.severity === "urgent" ? "text-red-500" : f.severity === "attention" ? "text-amber-500" : "text-stone-400"}`} />
                <div className="flex-1">
                  <p className="text-sm text-stone-900">{f.description}</p>
                  <p className="text-xs text-stone-400">{f.patient_name || f.patient_id || ""} &middot; {f.type?.replace(/_/g, " ")} &middot; {f.date?.split("T")[0]}</p>
                </div>
                <Badge variant="outline" className={`text-xs capitalize ${f.severity === "urgent" ? "bg-red-50 text-red-700 border-red-200" : f.severity === "attention" ? "bg-amber-50 text-amber-700 border-amber-200" : ""}`}>
                  {f.severity}
                </Badge>
              </Card>
            );
          }) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <Flag className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No operational flags. All operational items are on track.</p>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Tasks */}
      {tab === "tasks" && (
        <div className="space-y-2">
          <div className="flex gap-2 mb-3">
            {["all", "open", "in_progress", "completed"].map(s => (
              <Button key={s} size="sm" variant={statusFilter === s ? "default" : "outline"} className={`rounded-lg text-xs capitalize ${statusFilter === s ? "bg-stone-900 text-white" : ""}`} onClick={() => setStatusFilter(s)}>
                {s === "all" ? "All" : s.replace(/_/g, " ")}
              </Button>
            ))}
          </div>
          {tasks.length > 0 ? tasks.map(t => {
            const PIcon = t.priority === "urgent" ? AlertTriangle : t.priority === "high" ? AlertTriangle : t.priority === "medium" ? Clock : Circle;
            return (
              <Card key={t.task_id} className={`rounded-xl border-stone-200/60 p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${t.status === "completed" ? "opacity-60" : ""}`} data-testid={`task-${t.task_id}`}>
                <PIcon className={`w-5 h-5 shrink-0 ${priorityColor[t.priority]}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${t.status === "completed" ? "line-through text-stone-400" : "text-stone-900"}`}>{t.title}</p>
                  <p className="text-xs text-stone-400">{t.patient_name ? `Patient: ${t.patient_name} · ` : ""}{t.due_date ? `Due: ${t.due_date} · ` : ""}By: {t.created_by_name}</p>
                  {t.description && <p className="text-xs text-stone-500 mt-0.5">{t.description}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  {t.status === "open" && <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => handleTaskStatus(t.task_id, "in_progress")}>Start</Button>}
                  {(t.status === "open" || t.status === "in_progress") && (
                    <Button size="sm" className="text-xs rounded-lg bg-green-600 hover:bg-green-700 text-white" onClick={() => handleTaskStatus(t.task_id, "completed")}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Done
                    </Button>
                  )}
                </div>
              </Card>
            );
          }) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <HeartPulse className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No tasks found.</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default FollowUpsPage;
